import { useCallback, useEffect, useState } from 'react';
import { SessionTimeline, ESnackbarStyle } from 'shared/types';
import { getSessionTimeline } from 'services/api/sessionsService';
import { openNotification } from 'utils/notification-utils';

interface UseSessionTimelineResult {
  timeline: SessionTimeline | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Lazily fetches the per-session timeline (LE/station/progress/answer rows) used
 * by the "session clock". Fetches on mount and whenever `sessionId` changes.
 */
const useSessionTimeline = (sessionId?: string): UseSessionTimelineResult => {
  const [timeline, setTimeline] = useState<SessionTimeline | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeline = useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getSessionTimeline(sessionId);
      setTimeline(response.data);
    } catch (e: any) {
      const errorMessage = e?.message || 'Failed to load session timeline';
      setError(errorMessage);
      openNotification(ESnackbarStyle.ERROR, errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect((): void => {
    void fetchTimeline();
  }, [fetchTimeline]);

  return { timeline, isLoading, error, refetch: fetchTimeline };
};

export default useSessionTimeline;
